<template>
  <div class="default-layout">
    <!-- Header -->
    <header class="bg-white shadow-sm sticky top-0 z-40">
      <nav class="container mx-auto px-4">
        <div class="flex items-center justify-between h-16">
          <!-- Logo -->
          <router-link to="/" class="flex items-center space-x-2">
            <div v-if="settingsStore.settings.company_logo || settingsStore.settings.header_logo" class="w-12 h-12">
              <img 
                :src="settingsStore.settings.company_logo || settingsStore.settings.header_logo" 
                :alt="settingsStore.settings.company_name"
                class="w-full h-full object-contain rounded-lg"
              />
            </div>
            <div v-else class="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center">
              <span class="text-white font-bold text-xl">P</span>
            </div>
            <span class="font-bold text-xl text-gray-800">{{ settingsStore.settings.company_name || 'Pasticceria' }}</span>
          </router-link>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center space-x-8">
            <router-link 
              to="/store" 
              class="text-gray-600 hover:text-pink-500 transition-colors"
            >
              Prodotti
            </router-link>
            <router-link 
              to="/cart" 
              class="text-gray-600 hover:text-pink-500 transition-colors relative"
            >
              Carrello
              <span 
                v-if="cartItemsCount > 0"
                class="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
              >
                {{ cartItemsCount }}
              </span>
            </router-link>
            
            <div v-if="isAuthenticated" class="flex items-center space-x-4">
              <router-link 
                :to="dashboardRoute" 
                class="text-gray-600 hover:text-pink-500 transition-colors"
              >
                Dashboard
              </router-link>
              <button 
                @click="logout"
                class="text-gray-600 hover:text-pink-500 transition-colors"
              >
                Logout
              </button>
            </div>
            <div v-else class="flex items-center space-x-4">
              <router-link 
                to="/auth/login" 
                class="text-gray-600 hover:text-pink-500 transition-colors"
              >
                Accedi
              </router-link>
              <router-link 
                to="/auth/register" 
                class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
              >
                Registrati
              </router-link>
            </div>
          </div>

          <!-- Mobile menu button -->
          <button 
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="md:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path 
                v-if="!mobileMenuOpen"
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M4 6h16M4 12h16M4 18h16"
              />
              <path 
                v-else
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Mobile Navigation -->
        <div 
          v-if="mobileMenuOpen"
          class="md:hidden border-t py-4"
        >
          <div class="flex flex-col space-y-4">
            <router-link 
              to="/store" 
              @click="mobileMenuOpen = false"
              class="text-gray-600 hover:text-pink-500 transition-colors"
            >
              Prodotti
            </router-link>
            <router-link 
              to="/cart" 
              @click="mobileMenuOpen = false"
              class="text-gray-600 hover:text-pink-500 transition-colors"
            >
              Carrello ({{ cartItemsCount }})
            </router-link>
            
            <div v-if="isAuthenticated" class="flex flex-col space-y-4 pt-4 border-t">
              <router-link 
                :to="dashboardRoute" 
                @click="mobileMenuOpen = false"
                class="text-gray-600 hover:text-pink-500 transition-colors"
              >
                Dashboard
              </router-link>
              <button 
                @click="handleLogout"
                class="text-left text-gray-600 hover:text-pink-500 transition-colors"
              >
                Logout
              </button>
            </div>
            <div v-else class="flex flex-col space-y-4 pt-4 border-t">
              <router-link 
                to="/auth/login" 
                @click="mobileMenuOpen = false"
                class="text-gray-600 hover:text-pink-500 transition-colors"
              >
                Accedi
              </router-link>
              <router-link 
                to="/auth/register" 
                @click="mobileMenuOpen = false"
                class="text-pink-500 font-medium"
              >
                Registrati
              </router-link>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <!-- Main Content -->
    <main class="flex-1">
      <slot />
    </main>

    <!-- Footer -->
    <footer class="bg-gray-900 text-white mt-16">
      <div class="container mx-auto px-4 py-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- About -->
          <div>
            <h3 class="font-bold text-lg mb-4">{{ settingsStore.settings.footer_about_title || 'Chi Siamo' }}</h3>
            <p class="text-gray-400">
              {{ settingsStore.settings.footer_about_text || settingsStore.settings.company_description || 'La tua pasticceria di fiducia, dove tradizione e innovazione si incontrano.' }}
            </p>
          </div>

          <!-- Quick Links -->
          <div>
            <h3 class="font-bold text-lg mb-4">Link Utili</h3>
            <ul class="space-y-2">
              <li v-for="link in settingsStore.settings.footer_links" :key="link.name" v-if="link && link.active">
                <router-link :to="link.link" class="text-gray-400 hover:text-white">
                  {{ link.name }}
                </router-link>
              </li>
              <li>
                <router-link to="/store" class="text-gray-400 hover:text-white">
                  Prodotti
                </router-link>
              </li>
              <li>
                <router-link to="/cart" class="text-gray-400 hover:text-white">
                  Carrello
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Contact -->
          <div>
            <h3 class="font-bold text-lg mb-4">{{ settingsStore.settings.footer_contact_title || 'Contatti' }}</h3>
            <ul class="space-y-2 text-gray-400">
              <li v-if="settingsStore.settings.company_phone">
                Tel: <a :href="'tel:' + settingsStore.settings.company_phone" class="hover:text-white">{{ settingsStore.settings.company_phone }}</a>
              </li>
              <li v-if="settingsStore.settings.company_email">
                Email: <a :href="'mailto:' + settingsStore.settings.company_email" class="hover:text-white">{{ settingsStore.settings.company_email }}</a>
              </li>
              <li v-if="settingsStore.settings.company_address">
                {{ settingsStore.settings.company_address }}
              </li>
            </ul>
          </div>

          <!-- Social -->
          <div>
            <h3 class="font-bold text-lg mb-4">{{ settingsStore.settings.footer_social_title || 'Seguici' }}</h3>
            <div class="flex space-x-4">
              <a v-if="settingsStore.settings.social_facebook" :href="settingsStore.settings.social_facebook" target="_blank" class="text-gray-400 hover:text-white">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a v-if="settingsStore.settings.social_instagram" :href="settingsStore.settings.social_instagram" target="_blank" class="text-gray-400 hover:text-white">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 715.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div class="mt-8 pt-8 border-t border-gray-800">
          <div class="flex flex-col md:flex-row justify-between items-center">
            <p class="text-gray-400 mb-4 md:mb-0">{{ settingsStore.settings.footer_copyright || '© 2024 Pasticceria. Tutti i diritti riservati.' }}</p>
            <router-link 
              to="/auth/login"
              class="flex items-center gap-2 px-6 py-2 bg-gray-800 hover:bg-gray-700 text-pink-300 hover:text-pink-200 rounded-lg transition-all"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span class="font-medium">Area Riservata</span>
            </router-link>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useSettingsStore } from '@/stores/settings'

const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const settingsStore = useSettingsStore()

// Carica settings all'avvio
onMounted(() => {
  settingsStore.fetchSettings()
})

const mobileMenuOpen = ref(false)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const cartItemsCount = computed(() => cartStore.itemsCount)

const dashboardRoute = computed(() => {
  if (authStore.isAdmin || authStore.isManager) return '/admin/dashboard'
  if (authStore.isPOS) return '/pos/dashboard'
  return '/client/dashboard'
})

const logout = async () => {
  await authStore.logout()
  router.push('/')
}

const handleLogout = async () => {
  mobileMenuOpen.value = false
  await logout()
}
</script>

<style scoped>
.default-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
</style>
